import { ref } from "vue";
import { useApi, useToaster } from "@shopexperts/shared";
import { useQueryClient } from "@tanstack/vue-query";

export interface SubmitDraftLeadResult {
    success: boolean;
    leadId: number;
    isNewAccount: boolean;
}

/**
 * Composable for submitting a draft lead and converting it to an actual lead.
 * 
 * This is the final step in the progressive lead submission flow.
 * 
 * **Important**: Before calling `submitDraft`, you should:
 * 1. Ensure the draft is complete (use `useDraftLead` to check `isComplete`)
 * 2. Check if authentication is required (use `useDraftLeadAuthCheck`)
 * 3. If auth is required, authenticate the user first
 * 
 * @param options - Configuration options
 * @param options.showToast - Whether to show success/error toasts (default: true)
 * 
 * @example
 * ```ts
 * const { submitDraft, isSubmitting } = useSubmitDraftLead();
 * 
 * try {
 *   const result = await submitDraft('abc123...');
 *   console.log('Lead created:', result.leadId);
 *   
 *   if (result.isNewAccount) {
 *     // New account - invitation email was sent
 *   } else {
 *     // Existing account - lead added to their account
 *   }
 * } catch (error) {
 *   // Handle error (auth required, incomplete draft, etc.)
 * }
 * ```
 */
export function useSubmitDraftLead(options: { showToast?: boolean } = {}) {
    const { showToast = true } = options;
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const isSubmitting = ref(false);

    /**
     * Submit a draft lead and convert it to an actual lead.
     * 
     * @param token - The draft lead token
     * @returns Result containing the created lead ID and account status
     * @throws Error if submission fails (auth required, incomplete draft, etc.)
     */
    async function submitDraft(token: string): Promise<SubmitDraftLeadResult> {
        isSubmitting.value = true;

        try {
            const result = await api.public.draftLeads.submit({
                params: { token },
            });

            // A submitted draft is consumed by the API, so refetching these public draft
            // queries can only race into 404s for the now-invalid token.
            queryClient.removeQueries({ queryKey: ["public", "draft-leads", token], exact: true });
            queryClient.removeQueries({ queryKey: ["public", "draft-leads", token, "check-auth"], exact: true });
            // Also invalidate client leads list since we created a new lead
            queryClient.invalidateQueries({ queryKey: ["client", "leads"] });

            if (showToast) {
                const description = result.is_new_account
                    ? "Your project has been submitted. Check your email for account setup instructions."
                    : "Your project has been submitted successfully. We'll match you with the right expert soon.";

                toaster.create({
                    title: "Lead submitted!",
                    description,
                    type: "success",
                });
            }

            return {
                success: true,
                leadId: result.lead_id,
                isNewAccount: result.is_new_account,
            };
        } catch (error: any) {
            console.error("[useSubmitDraftLead] Submit error:", error);

            if (showToast) {
                // Provide more specific error messages based on common failure cases
                let title = "Submission failed";
                let description = error?.response?.data?.message || "Failed to submit your lead. Please try again.";

                // Check for specific error cases
                if (error?.response?.status === 401) {
                    title = "Authentication required";
                    description = "Please log in to submit this lead.";
                } else if (error?.response?.status === 422) {
                    title = "Draft incomplete";
                    description = error?.response?.data?.message || "Please fill in all required fields before submitting.";
                }

                toaster.create({
                    title,
                    description,
                    type: "error",
                });
            }

            throw error;
        } finally {
            isSubmitting.value = false;
        }
    }

    return {
        submitDraft,
        isSubmitting,
    };
}
